import React, { useState, useEffect } from 'react';
import { Container, Card, Badge, Button, Spinner, Alert } from 'react-bootstrap';
import { FaBell, FaBellSlash, FaChevronLeft } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getNotifications, startNotificationPolling } from '../../services/notificationService';
import './AlertsPage.css';

const AlertsPage = () => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { currentUser } = useAuth();

  const fetchAlerts = async () => {
    try {
      setLoading(true);
      const notifications = await getNotifications();
      
      // Transform backend data to match component structure
      const transformedAlerts = notifications.map(notification => ({
        id: notification.id,
        title: notification.title,
        message: notification.body,
        timestamp: notification.created_at,
        read: false, // Backend doesn't track read status yet, so default to false
        type: notification.type
      }));
      
      setAlerts(transformedAlerts);
      setError('');
    } catch (err) {
      console.error('Error fetching alerts:', err);
      setError('Failed to load alerts. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentUser) {
      fetchAlerts();
      
      // Start polling for real-time updates every 30 seconds
      const stopPolling = startNotificationPolling((notifications) => {
        const transformedAlerts = notifications.map(notification => ({
          id: notification.id,
          title: notification.title,
          message: notification.body,
          timestamp: notification.created_at,
          read: false,
          type: notification.type
        }));
        setAlerts(transformedAlerts);
      }, 30000);
      
      // Cleanup polling on unmount
      return () => {
        stopPolling();
      };
    }
  }, [currentUser]);

  const markAsRead = (alertId) => {
    setAlerts(alerts.map(alert => 
      alert.id === alertId ? { ...alert, read: true } : alert
    ));
  };

  const markAllAsRead = () => {
    setAlerts(alerts.map(alert => ({ ...alert, read: true })));
  };

  const getBadgeVariant = (type) => {
    switch (type) {
      case 'order':
        return 'success';
      case 'promotion':
        return 'warning';
      case 'system':
        return 'info';
      default:
        return 'secondary';
    }
  };

  const formatDate = (dateString) => {
    const options = { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(dateString).toLocaleString(undefined, options);
  };

  return (
    <Container className="alerts-page py-4">
      <div className="d-flex align-items-center mb-4">
        <Link to="/" className="me-3 text-dark">
          <FaChevronLeft size={20} />
        </Link>
        <h2 className="mb-0">Notifications</h2>
        {alerts.some(alert => !alert.read) && (
          <Button 
            variant="link" 
            className="ms-auto"
            onClick={markAllAsRead}
            disabled={loading}
          >
            Mark all as read
          </Button>
        )}
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2">Loading notifications...</p>
        </div>
      ) : alerts.length === 0 ? (
        <div className="text-center py-5">
          <FaBellSlash size={48} className="text-muted mb-3" />
          <h5>No notifications yet</h5>
          <p className="text-muted">We'll notify you when there's something new.</p>
        </div>
      ) : (
        <div className="alerts-list">
          {alerts.map((alert) => (
            <Card 
              key={alert.id} 
              className={`mb-3 ${!alert.read ? 'border-start border-3 border-primary' : ''}`}
              onClick={() => markAsRead(alert.id)}
              style={{ cursor: 'pointer' }}
            >
              <Card.Body>
                <div className="d-flex align-items-start">
                  <div className="flex-grow-1">
                    <div className="d-flex align-items-center mb-1">
                      <h6 className="mb-0">{alert.title}</h6>
                      <Badge 
                        bg={getBadgeVariant(alert.type)}
                        className="ms-2"
                        pill
                      >
                        {alert.type}
                      </Badge>
                      {!alert.read && (
                        <span className="ms-auto badge bg-primary rounded-pill">New</span>
                      )}
                    </div>
                    <p className="mb-1">{alert.message}</p>
                    <small className="text-muted">
                      {formatDate(alert.timestamp)}
                    </small>
                  </div>
                </div>
              </Card.Body>
            </Card>
          ))}
        </div>
      )}
    </Container>
  );
};

export default AlertsPage;
